import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Star } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { MockBadge } from '../components/MockBadge';
import { Sheet, SheetContent, SheetBody, SheetFooter } from '../components/ui/Sheet';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime, formatNumber } from '../lib/format';
import type {
  ReviewItem,
  ReviewItemListResponse,
  ReviewItemStatus,
  ReviewStats,
} from '../types/api';

const STATUS_LABELS: Record<ReviewItemStatus, string> = {
  open: 'Open',
  resolved: 'Resolved',
};

function ratingLabel(rating: number | null): string {
  return rating == null ? 'No rating' : `${rating} out of 5`;
}

/**
 * Follow-up queue for the anmeldelsesflow (28/9, docs/decisions.md
 * "Anmeldelsesflow 28/9"): a rating of 1-3, or any free-text feedback, from
 * the public `/review/:token` page lands here so an editor+ can help the
 * customer — this list never shows or edits the Trustpilot-facing rating
 * itself (that decision lives only on the public page, by design, per the
 * task's HARD RULE — no review gating). Full feedback text is shown only in
 * the right-hand side panel, never fullscreen, matching KundemaerkaterPage.
 */
export function AnmeldelserPage() {
  const { isEditor } = useAuth();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<ReviewItemStatus | 'all'>('open');
  const [openId, setOpenId] = useState<string | null>(null);

  // Sikkerhedsreview 28/9, finding 10: `feedback` is personal data, so the
  // list/detail follow-up queue is editor+ (`reviews.repo.ts`'s
  // `requireRole(ctx, 'editor')`) — a viewer would just get a 403 on
  // these, so don't even fetch. Stats stays viewer+ (no contact data).
  const { data, isLoading } = useQuery({
    queryKey: ['reviews', statusFilter],
    queryFn: () =>
      apiFetch<ReviewItemListResponse>(
        statusFilter === 'all' ? '/reviews' : `/reviews?status=${statusFilter}`,
      ),
    enabled: isEditor,
  });

  const { data: stats } = useQuery({
    queryKey: ['reviews', 'stats'],
    queryFn: () => apiFetch<ReviewStats>('/reviews/stats'),
  });

  const { data: openItem } = useQuery({
    queryKey: ['reviews', 'item', openId],
    queryFn: () => apiFetch<ReviewItem>(`/reviews/${openId}`),
    enabled: Boolean(openId) && isEditor,
  });

  const resolveMutation = useMutation({
    mutationFn: (id: string) => apiFetch<ReviewItem>(`/reviews/${id}/resolve`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reviews'] });
      setOpenId(null);
    },
  });

  const items = data?.items ?? [];
  const totalRatings = stats?.total ?? 0;

  return (
    <>
      <PageHeader
        title="Reviews"
        badge={<MockBadge />}
        description="This only collects the cases that need follow-up — a rating of 3 or below, or a message in the feedback form. The Trustpilot button on the public page always looks the same for every rating and cannot be hidden or changed from here."
      />

      {stats ? (
        <Card className="p-4 flex flex-col gap-2 max-w-[560px]">
          <h2 className="text-sm font-semibold m-0">
            Total ratings ({formatNumber(totalRatings)})
          </h2>
          <div className="flex items-end gap-3">
            {(['1', '2', '3', '4', '5'] as const).map((star) => {
              const count = stats.byRating[star];
              const pct = totalRatings > 0 ? Math.round((count / totalRatings) * 100) : 0;
              return (
                <div key={star} className="flex flex-col items-center gap-1 flex-grow">
                  <div className="w-full h-16 rounded bg-bg flex items-end overflow-hidden">
                    <div
                      className="w-full bg-accent"
                      style={{ height: `${pct}%` }}
                      aria-hidden="true"
                    />
                  </div>
                  <div className="text-xs text-muted">{star}★</div>
                  <div className="text-xs font-mono">{formatNumber(count)}</div>
                </div>
              );
            })}
          </div>
        </Card>
      ) : null}

      {!isEditor ? (
        <Alert>
          You have read-only access to the ratings above. The follow-up queue itself — with the
          feedback text — requires editor access, since it contains personal data. Contact an
          editor.
        </Alert>
      ) : (
        <>
          <div className="flex justify-end gap-2">
            {(['open', 'resolved', 'all'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                className={
                  'px-3 py-1.5 rounded-[9px] text-sm border ' +
                  (statusFilter === s
                    ? 'bg-accent-soft text-link border-transparent font-medium'
                    : 'border-border text-zinc-700 hover:bg-bg')
                }
              >
                {s === 'open' ? 'Open' : s === 'resolved' ? 'Resolved' : 'All'}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="text-muted">Loading…</div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Star />}
              title="No cases to show"
              description="Low ratings and feedback messages that need a follow-up show up here."
            />
          ) : (
            <div className="border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left text-xs text-muted uppercase tracking-wide">
                    <th className="px-4 py-2.5 font-medium">Order</th>
                    <th className="px-4 py-2.5 font-medium">Rating</th>
                    <th className="px-4 py-2.5 font-medium">Message</th>
                    <th className="px-4 py-2.5 font-medium">Status</th>
                    <th className="px-4 py-2.5 font-medium">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="border-t border-border-soft">
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setOpenId(item.id)}
                          className="text-left"
                        >
                          <StatusBadge
                            tone={item.status === 'open' ? 'warning' : 'success'}
                            icon="●"
                          >
                            {item.orderId}
                          </StatusBadge>
                        </button>
                      </td>
                      <td className="px-4 py-3">{ratingLabel(item.rating)}</td>
                      <td className="px-4 py-3 text-muted truncate max-w-[220px]">
                        {item.feedback || '–'}
                      </td>
                      <td className="px-4 py-3">{STATUS_LABELS[item.status]}</td>
                      <td className="px-4 py-3 text-muted">{formatDateTime(item.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <Sheet
        open={Boolean(openId)}
        onOpenChange={(open) => {
          if (!open) setOpenId(null);
        }}
      >
        {openId ? (
          <SheetContent
            eyebrow="Review"
            title={openItem ? `Order ${openItem.orderId}` : 'Loading…'}
            description="The rating is for internal follow-up only and never affects the public Trustpilot button."
          >
            <SheetBody>
              {openItem ? (
                <>
                  <div className="flex items-center gap-2">
                    <StatusBadge tone={openItem.status === 'open' ? 'warning' : 'success'} icon="●">
                      {STATUS_LABELS[openItem.status]}
                    </StatusBadge>
                    <span className="text-sm text-muted">{ratingLabel(openItem.rating)}</span>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <div className="text-xs font-medium text-muted uppercase tracking-wide">
                      Feedback from the customer
                    </div>
                    <p className="m-0 text-sm whitespace-pre-wrap">
                      {openItem.feedback || 'No free text — rating only.'}
                    </p>
                  </div>
                  <p className="m-0 text-xs text-muted">
                    Received {formatDateTime(openItem.createdAt)}
                    {openItem.resolvedAt
                      ? ` · Resolved ${formatDateTime(openItem.resolvedAt)}${
                          openItem.resolvedBy ? ` by ${openItem.resolvedBy}` : ''
                        }`
                      : ''}
                  </p>
                  {!isEditor ? (
                    <Alert>
                      You have read-only access. Contact an editor to mark the case resolved.
                    </Alert>
                  ) : null}
                  {resolveMutation.isError ? (
                    <Alert>Could not mark the case resolved. Try again.</Alert>
                  ) : null}
                </>
              ) : (
                <div className="text-muted">Loading…</div>
              )}
            </SheetBody>
            <SheetFooter>
              <Button variant="secondary" className="flex-grow" onClick={() => setOpenId(null)}>
                Close
              </Button>
              {isEditor && openItem && openItem.status === 'open' ? (
                <Button
                  className="flex-grow"
                  disabled={resolveMutation.isPending}
                  onClick={() => resolveMutation.mutate(openItem.id)}
                >
                  {resolveMutation.isPending ? 'Saving…' : 'Mark resolved'}
                </Button>
              ) : null}
            </SheetFooter>
          </SheetContent>
        ) : null}
      </Sheet>
    </>
  );
}
